import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../lib/api";
import { PortalPage, Section, Pill } from "../../components/portal/PortalPage";
import { ProgressStepper } from "../../components/portal/ProgressStepper";
import { NoticeBox } from "../../components/NoticeBox";
import { AttachmentGallery } from "../../components/portal/AttachmentGallery";
import { ClientTimeline } from "../../components/portal/ClientTimeline";
import { ClientNotesThread } from "../../components/portal/ClientNotesThread";
import { usePortal } from "../../components/portal/usePortal";
import { InvoiceSummaryCard } from "../../components/portal/InvoiceSummaryCard";
import { fmtDateTime, EMERGENCY_STATUS, URGENCY, titleCase } from "../../components/portal/portalUtils";
import { MapPin, Phone, Truck, ChatCircle } from "@phosphor-icons/react";
import { DetailSkeleton } from "../../components/Skeletons";
import { ScopeText, scopeLines } from "../../components/jobs/ScopeOfWorksField";
import { useTeamLabel } from "../../components/portal/PortalBrand";
import { MadeSafePanel } from "../../components/portal/MadeSafePanel";

export default function ClientEmergencyDetailPage() {
  const teamLabel = useTeamLabel();
  const { id } = useParams();
  const { perms } = usePortal();
  const [e, setE] = useState(null);
  const load = useCallback(() => API.get(`/client-hub/emergencies/${id}`).then(({ data }) => setE(data)).catch(() => setE(false)), [id]);
  useEffect(() => { load(); }, [load]);
  if (e === null) return <DetailSkeleton />;
  if (e === false) return <PortalPage title="Emergency not found" back={{ to: "/portal/emergencies", label: "Emergencies" }} />;

  return (
    <PortalPage
      title={e.title}
      subtitle={<span className="flex items-center gap-2 flex-wrap"><span className="font-mono">{e.reference}</span><Pill map={URGENCY} value={e.urgency} /><Pill map={EMERGENCY_STATUS} value={e.status} /><span className="text-[11px]">{e.response_hours}h response window</span></span>}
      back={{ to: "/portal/emergencies", label: "Emergencies" }}
      testid="emergency-detail"
    >
      <NoticeBox notice={e.notice} className="mb-6" testid="emergency-notice" />
      <Section title="Progress" className="mb-6"><ProgressStepper progress={e.progress} /></Section>
      <MadeSafePanel madeSafe={e.made_safe} jobId={id} reference={e.reference} />
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title="What was reported">
            <p className="text-sm whitespace-pre-wrap">{e.description || <span className="text-muted-foreground">No further details given.</span>}</p>
            <dl className="grid sm:grid-cols-2 gap-3 mt-4 text-xs">
              <div><dt className="text-muted-foreground">Reported</dt><dd className="font-medium">{fmtDateTime(e.created_at)}{e.created_by_name ? ` by ${e.created_by_name.replace(" (Client)", "")}` : ""}</dd></div>
              <div><dt className="text-muted-foreground">Acknowledged</dt><dd className="font-medium">{fmtDateTime(e.acknowledged_at)}</dd></div>
              {e.made_safe_at && <div><dt className="text-muted-foreground">Made safe</dt><dd className="font-medium text-emerald-700 dark:text-emerald-400" data-testid="emergency-made-safe-at">{fmtDateTime(e.made_safe_at)}</dd></div>}
              <div><dt className="text-muted-foreground">Resolved</dt><dd className="font-medium">{fmtDateTime(e.completed_at)}</dd></div>
              <div><dt className="text-muted-foreground">Category</dt><dd className="font-medium">{titleCase(e.category)}</dd></div>
            </dl>
          </Section>
          {scopeLines(e.scope_of_works).length > 0 && (
            <Section title="Scope of Works" testid="emergency-scope-of-works">
              <ScopeText text={e.scope_of_works} testid="emergency-scope-of-works-text" />
            </Section>
          )}
          <Section title={`Photos & Files (${(e.attachments || []).length})`}><AttachmentGallery attachments={e.attachments || []} /></Section>
          <Section title={<span className="flex items-center gap-1.5"><ChatCircle size={14} /> Conversation</span>} testid="emergency-comments">
            <ClientNotesThread items={e.client_comments || []} postUrl={`/client-hub/emergencies/${id}/comments`} folder="client-emergencies" canMessage={perms.can_message} onSent={load} testPrefix="emergency-comment" />
          </Section>
        </div>
        <div className="space-y-6">
          <Section title={teamLabel.heading}>
            {e.contractor ? (
              <p className="text-sm font-medium flex items-center gap-2"><Truck size={16} className="text-primary" /> {e.contractor}</p>
            ) : e.contractor_assigned ? (
              <p className="text-sm font-medium flex items-center gap-2 text-emerald-700 dark:text-emerald-400" data-testid="emg-contractor-assigned"><Truck size={16} /> {teamLabel.dispatched}</p>
            ) : <p className="text-xs text-muted-foreground">Being dispatched — we'll update you as soon as {teamLabel.brand ? "the team is" : "a contractor is"} assigned.</p>}
            {e.arrived_at && <p className="text-[11px] text-emerald-600 mt-1">Arrived on site {fmtDateTime(e.arrived_at)}</p>}
          </Section>
          <Section title="Location">
            <p className="text-sm font-medium flex items-start gap-1.5"><MapPin size={16} className="text-primary shrink-0 mt-0.5" /> {e.property_name ? `${e.property_name} — ` : ""}{e.address}</p>
            {(e.site_contact_name || e.site_contact_phone) && <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Phone size={12} /> {e.site_contact_name} {e.site_contact_phone}</p>}
          </Section>
          <InvoiceSummaryCard invoices={e.invoice} testid="emergency-invoice" />
          <Section title="Activity"><ClientTimeline entries={e.timeline} /></Section>
        </div>
      </div>
    </PortalPage>
  );
}
